import { useRef, type KeyboardEvent, type PointerEvent } from 'react'
import { normalizarGraus, pontoCardeal } from '../../../dominio/vento'

const TAMANHO = 168
const CENTRO = TAMANHO / 2
const RAIO = 62

/**
 * Rosa dos ventos arrastável. O ponteiro marca DE ONDE o vento vem (como a
 * biruta e o boletim); a seta clara mostra para onde a pluma vai. Arrastar
 * ou tocar escolhe a direção; setas do teclado giram de 5 em 5 graus.
 */
export function RosaDosVentos({ direcaoGraus, aoMudar }: { direcaoGraus: number; aoMudar: (graus: number) => void }) {
  const svg = useRef<SVGSVGElement>(null)
  const arrastando = useRef(false)

  const atualizarPeloPonteiro = (evento: PointerEvent<SVGSVGElement>) => {
    const caixa = svg.current?.getBoundingClientRect()
    if (!caixa) return
    const dx = evento.clientX - (caixa.left + caixa.width / 2)
    const dy = evento.clientY - (caixa.top + caixa.height / 2)
    if (Math.hypot(dx, dy) < 6) return
    // norte para cima, sentido horário
    aoMudar(Math.round(normalizarGraus((Math.atan2(dx, -dy) * 180) / Math.PI)))
  }

  const aoTeclar = (evento: KeyboardEvent<SVGSVGElement>) => {
    const passo = evento.shiftKey ? 45 : 5
    if (evento.key === 'ArrowRight' || evento.key === 'ArrowUp') aoMudar(normalizarGraus(direcaoGraus + passo))
    else if (evento.key === 'ArrowLeft' || evento.key === 'ArrowDown') aoMudar(normalizarGraus(direcaoGraus - passo))
    else return
    evento.preventDefault()
  }

  const rad = (direcaoGraus * Math.PI) / 180
  const deOnde = [CENTRO + Math.sin(rad) * RAIO, CENTRO - Math.cos(rad) * RAIO] as const
  const paraOnde = [CENTRO - Math.sin(rad) * (RAIO - 14), CENTRO + Math.cos(rad) * (RAIO - 14)] as const

  return (
    <svg
      ref={svg}
      className="rosa-ventos"
      width={TAMANHO}
      height={TAMANHO}
      viewBox={`0 0 ${TAMANHO} ${TAMANHO}`}
      role="slider"
      tabIndex={0}
      aria-label="Direção de onde o vento vem"
      aria-valuemin={0}
      aria-valuemax={359}
      aria-valuenow={Math.round(direcaoGraus)}
      aria-valuetext={`de ${pontoCardeal(direcaoGraus)}, ${Math.round(direcaoGraus)} graus`}
      onKeyDown={aoTeclar}
      onPointerDown={(e) => {
        arrastando.current = true
        e.currentTarget.setPointerCapture(e.pointerId)
        atualizarPeloPonteiro(e)
      }}
      onPointerMove={(e) => arrastando.current && atualizarPeloPonteiro(e)}
      onPointerUp={() => {
        arrastando.current = false
      }}
    >
      <circle cx={CENTRO} cy={CENTRO} r={RAIO + 10} className="rosa-ventos__fundo" />
      <circle cx={CENTRO} cy={CENTRO} r={RAIO} className="rosa-ventos__aro" />
      {(['N', 'L', 'S', 'O'] as const).map((rotulo, i) => {
        const a = (i * Math.PI) / 2
        return (
          <text key={rotulo} x={CENTRO + Math.sin(a) * (RAIO - 12)} y={CENTRO - Math.cos(a) * (RAIO - 12) + 4} className="rosa-ventos__cardeal">
            {rotulo}
          </text>
        )
      })}
      {/* a favor do vento: para onde a pluma vai */}
      <line x1={CENTRO} y1={CENTRO} x2={paraOnde[0]} y2={paraOnde[1]} className="rosa-ventos__pluma" />
      {/* de onde vem: o ponteiro que se arrasta */}
      <line x1={CENTRO} y1={CENTRO} x2={deOnde[0]} y2={deOnde[1]} className="rosa-ventos__ponteiro" />
      <circle cx={deOnde[0]} cy={deOnde[1]} r={9} className="rosa-ventos__pega" />
      <circle cx={CENTRO} cy={CENTRO} r={4} className="rosa-ventos__centro" />
    </svg>
  )
}
